/**
 * Color Swatches Component
 * Handles swatch hover interactions for product cards
 * Updates swatch selection when variant changes on PDP
 */

class ColorSwatches extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.setupSwatchHover();
    this.setupVariantChangeListener();
    this.initializeSelectedSwatch();
  }

  initializeSelectedSwatch() {
    // Only initialize on PDP (not collection pages)
    if (this.classList.contains('color-swatches--collection')) {
      return;
    }

    // Use requestAnimationFrame to ensure DOM is fully ready
    requestAnimationFrame(() => {
      // Get initial variant from product data
      const productDataScript = document.querySelector(`[id^="product-data-"]`);
      if (!productDataScript) return;

      try {
        const productData = JSON.parse(productDataScript.textContent);
        const initialVariant = productData.initialVariant;
        const variantOptions = productData.variantOptions;

        if (initialVariant && variantOptions) {
          this.updateSelectedSwatch(initialVariant, variantOptions);
        }
      } catch (e) {
        console.error('Error initializing color swatch:', e);
      }
    });
  }

  setupVariantChangeListener() {
    // Only set up variant change listener on PDP (not collection pages)
    if (this.classList.contains('color-swatches--collection')) {
      return;
    }

    // Listen for variant change events from variant-selects or variant-radios
    document.addEventListener('variant:change', (event) => {
      if (event.detail && event.detail.variant) {
        this.updateSelectedSwatch(event.detail.variant, event.detail.variantOptions);
      }
    });
  }

  updateSelectedSwatch(variant, variantOptions) {
    if (!variant || !variantOptions) return;

    // Find the color option index (Kolor or Color)
    let colorOptionIndex = -1;
    for (let i = 0; i < variantOptions.length; i++) {
      const optionName = variantOptions[i].toLowerCase();
      if (optionName === 'kolor' || optionName === 'color') {
        colorOptionIndex = i;
        break;
      }
    }

    // If no color option found, return
    if (colorOptionIndex === -1 || !variant.options || !variant.options[colorOptionIndex]) {
      return;
    }

    // Get the color value from the variant
    const colorValue = variant.options[colorOptionIndex];

    // Find all swatches and remove selected classes
    const allSwatches = this.querySelectorAll('.color-swatch');
    const allWrappers = this.querySelectorAll('.color-swatch__wrapper');

    allSwatches.forEach((swatch) => {
      swatch.classList.remove('color-swatch--selected', 'color-swatch--main');
    });

    allWrappers.forEach((wrapper) => {
      wrapper.classList.remove('color-swatch--selected', 'color-swatch--main');
    });

    // Find and select only the first matching swatch (same color can appear multiple times)
    for (const swatch of allSwatches) {
      const swatchColorName = swatch.dataset.swatchColorName;
      if (swatchColorName && swatchColorName.toLowerCase() === colorValue.toLowerCase()) {
        swatch.classList.add('color-swatch--selected', 'color-swatch--main');
        const wrapper = swatch.closest('.color-swatch__wrapper');
        if (wrapper) {
          wrapper.classList.add('color-swatch--selected', 'color-swatch--main');
        }
        break;
      }
    }
  }

  setupSwatchHover() {
    const swatches = this.querySelectorAll('.color-swatch');

    swatches.forEach((swatch) => {
      swatch.addEventListener('mouseenter', (e) => {
        this.handleSwatchHover(e.target);
      });

      swatch.addEventListener('mouseleave', (e) => {
        this.handleSwatchLeave(e.target);
      });
    });
  }

  handleSwatchHover(swatch) {
    const productImage = swatch.dataset.productImage;
    if (!productImage) {
      return;
    }

    // Only work if this is a collection swatch
    if (!this.classList.contains('color-swatches--collection')) {
      return;
    }

    // Find the product card container
    const productCard = this.closest('.product-item') || this.closest('.card-wrapper');
    if (!productCard) {
      return;
    }

    // Find the main product image (the bottom one that's currently visible)
    const mainImage = productCard.querySelector('.card__media--main');
    const mediaElement = productCard.querySelector('.card__media .media');
    console.log('mainImage', mainImage);
    if (!mainImage) {
      return;
    }

    // Store original image source and srcset
    if (!mainImage.dataset.originalSrc) {
      mainImage.dataset.originalSrc = mainImage.src;
    }
    if (!mainImage.dataset.originalSrcset) {
      mainImage.dataset.originalSrcset = mainImage.srcset;
    }

    // Add hover class to media element
    if (mediaElement) {
      mediaElement.classList.add('media--hover-swatch');
    }

    // Update image source - set src and remove srcset to use the single image
    mainImage.src = productImage;
    mainImage.srcset = '';
    mainImage.style.transition = 'opacity 0.3s ease';
    mainImage.style.opacity = '1';
  }

  handleSwatchLeave(swatch) {
    const productCard = this.closest('.product-item') || this.closest('.card-wrapper');
    if (!productCard) {
      return;
    }

    const mainImage = productCard.querySelector('.card__media--main');
    const mediaElement = productCard.querySelector('.card__media .media');
    if (!mainImage || !mainImage.dataset.originalSrc) {
      return;
    }

    // Remove hover class from media element
    if (mediaElement) {
      mediaElement.classList.remove('media--hover-swatch');
    }

    // Restore original image and srcset
    mainImage.src = mainImage.dataset.originalSrc;
    if (mainImage.dataset.originalSrcset) {
      mainImage.srcset = mainImage.dataset.originalSrcset;
    }
    mainImage.style.opacity = '1';
  }
}

customElements.define('color-swatches', ColorSwatches);
